/**
 * Merch /generate artwork flags by favourite (station) type.
 * Extend ARTWORK_TYPE_FLAG_RULES when new types need special API fields.
 *
 * - Matching types: send is_transparent + is_logo as configured
 * - Everything else: omit both params
 */

const DEFAULT_MERCH_ARTWORK_FLAGS = Object.freeze({
  is_logo: false,
  is_transparent: false,
  /** When false, FormData omits both params entirely. */
  include: false,
});

/**
 * @typedef {{ is_logo?: boolean, is_transparent?: boolean, include?: boolean }} MerchArtworkFlags
 * @typedef {{ match: RegExp, flags: MerchArtworkFlags }} MerchArtworkFlagRule
 */

/** @type {MerchArtworkFlagRule[]} */
const ARTWORK_TYPE_FLAG_RULES = [
  {
    // Caricature portrait
    match: /^caricature\s*portrait$/i,
    flags: { include: true, is_logo: true, is_transparent: true },
  },
  {
    // Snap & Print (gallery label: Print Your Own)
    match: /^snap\s*&\s*print$/i,
    flags: { include: true, is_logo: true, is_transparent: true },
  },
  {
    // Print Your Own (gallery label: Print Your Own)
    match: /^print\s*your\s*own$/i,
    flags: { include: true, is_logo: true, is_transparent: true },
  },
  {
    // Words of the Heart (gallery label: Words of the Heart)
    match: /^words\s*of\s*the\s*heart$/i,
    flags: { include: true, is_logo: false, is_transparent: true },
  },
  {
    // Holding Hands (gallery label: Holding Hands)
    match: /^holding\s*hands$/i,
    flags: { include: true, is_logo: false, is_transparent: true },
  },
  {
    // Graphic Heart / A Place In My Heart
    match: /^(graphic\s*heart|a\s*place\s*in\s*my\s*heart)$/i,
    flags: { include: true, is_logo: false, is_transparent: true },
  },
  {
    // Your Voice Becomes Art (gallery label: Your Voice Becomes Art)
    match: /^your\s*voice\s*becomes\s*art$/i,
    flags: { include: true, is_logo: false, is_transparent: true },
  },
  {
    // Tracing Hand (gallery label: Tracing Hand)
    match: /^tracing\s*hand$/i,
    flags: { include: true, is_logo: false, is_transparent: true },
  },
];

/** Normalize favourite / station type labels for matching. */
export function normalizeArtworkType(type) {
  return String(type || "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Resolve FormData flags for creative-system/merch/generate from artwork type.
 * @param {string} [type] favourite item.type / station name
 * @returns {{ is_logo: boolean, is_transparent: boolean, include: boolean }}
 */
export function getMerchGenerateArtworkFlags(type) {
  const normalized = normalizeArtworkType(type);
  if (!normalized) {
    return { ...DEFAULT_MERCH_ARTWORK_FLAGS };
  }

  for (const rule of ARTWORK_TYPE_FLAG_RULES) {
    if (rule.match.test(normalized)) {
      return {
        ...DEFAULT_MERCH_ARTWORK_FLAGS,
        ...rule.flags,
      };
    }
  }

  return { ...DEFAULT_MERCH_ARTWORK_FLAGS };
}

/**
 * Append artwork flags to merch generate FormData when the type has a rule.
 * Unmatched types omit both params.
 * @param {FormData} form
 * @param {MerchArtworkFlags} flags
 */
export function appendMerchArtworkFlags(form, flags) {
  const resolved = {
    ...DEFAULT_MERCH_ARTWORK_FLAGS,
    ...(flags || {}),
  };
  if (!resolved.include) return;
  form.append("is_transparent", resolved.is_transparent ? "true" : "false");
  form.append("is_logo", resolved.is_logo ? "true" : "false");
}
